import { Injectable, signal, computed } from '@angular/core';
import { WebSocketService } from './websocket.service';
import { BatchService } from './batch.service';

export interface DownloadTask {
  requestId: string;
  batchId: string;
  batchRef: string;
  /** queued = waiting for zip_ready | downloading = fetching blob | done = saved | failed */
  status: 'queued' | 'downloading' | 'done' | 'failed';
  reason?: string;
  filename: string;
}

/**
 * Orchestrates the async QR-ZIP download flow:
 *   1. POST /batches/:id/qrcodes/download   → get requestId
 *   2. Listen for zip_ready / zip_failed WS events
 *   3. GET  /batches/downloads/:requestId   → trigger browser file save
 *
 * Exposes a `tasks` signal consumed by DownloadToastComponent.
 */
@Injectable({ providedIn: 'root' })
export class DownloadTaskService {
  readonly tasks    = signal<DownloadTask[]>([]);
  readonly expanded = signal(true);

  private readonly pendingBatchRequests = new Set<string>();
  private readonly inFlightDownloads = new Set<string>();

  readonly activeCount = computed(
    () => this.tasks().filter(t => t.status === 'queued' || t.status === 'downloading').length,
  );
  readonly doneOrFailedCount = computed(
    () => this.tasks().filter(t => t.status === 'done' || t.status === 'failed').length,
  );

  constructor(
    private wsService: WebSocketService,
    private batchService: BatchService,
  ) {
    // Try to connect immediately (succeeds if user is already authenticated).
    this.wsService.connect();

    this.wsService
      .on<{ requestId: string; batchId: string; batchRef: string }>('zip_ready')
      .subscribe(p => this.handleZipReady(p));

    this.wsService
      .on<{ requestId: string; batchId: string; batchRef: string; reason: string }>('zip_failed')
      .subscribe(p => this.handleZipFailed(p));
  }

  /**
   * Queue a ZIP download for one batch.
   * Call once per batch; for multi-select call once per selected batch.
   */
  requestDownload(batchId: string, batchRef: string): void {
    if (this.pendingBatchRequests.has(batchId) || this.hasActiveTaskForBatch(batchId)) {
      this.expanded.set(true);
      return;
    }

    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const filename = `QR_Codes_${batchRef}_${dateStr}.zip`;

    // Ensure WS is connected (idempotent if already open).
    this.wsService.connect();
    // Auto-expand the panel so the user notices.
    this.expanded.set(true);

    this.pendingBatchRequests.add(batchId);

    this.batchService.requestBatchDownload(batchId).subscribe({
      next: (res) => {
        this.pendingBatchRequests.delete(batchId);

        this.updateTasks(list => {
          const activeForBatch = list.find(
            t => t.batchId === batchId && (t.status === 'queued' || t.status === 'downloading'),
          );
          if (activeForBatch) return list;

          const existingByRequest = list.find(t => t.requestId === res.result.requestId);
          if (existingByRequest) return list;

          return [
            ...list,
            { requestId: res.result.requestId, batchId, batchRef, status: 'queued', filename },
          ];
        });
      },
      error: (err: Error) => {
        this.pendingBatchRequests.delete(batchId);

        // Show a failed task immediately so the user is aware.
        this.updateTasks(list => [
          ...list,
          {
            requestId: `err-${Date.now()}-${batchId}`,
            batchId,
            batchRef,
            status: 'failed',
            reason: err?.message || 'Could not queue download',
            filename,
          },
        ]);
      },
    });
  }

  removeTask(requestId: string): void {
    this.updateTasks(list => list.filter(t => t.requestId !== requestId));
  }

  /** Dismiss all done/failed tasks; keep in-progress ones. */
  clearCompleted(): void {
    this.updateTasks(list =>
      list.filter(t => t.status === 'queued' || t.status === 'downloading'),
    );
  }

  // ── Private WS handlers ──────────────────────────────────────────────────

  private handleZipReady(payload: { requestId: string }): void {
    const task = this.tasks().find(t => t.requestId === payload.requestId);
    if (!task) return;

    this.downloadFromReadyEvent(task);
  }

  private handleZipFailed(payload: { requestId: string; reason?: string }): void {
    this.updateTasks(list =>
      list.map(t =>
        t.requestId === payload.requestId
          ? { ...t, status: 'failed', reason: payload.reason || 'ZIP generation failed' }
          : t,
      ),
    );
  }

  private triggerBrowserDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private hasActiveTaskForBatch(batchId: string): boolean {
    return this.tasks().some(
      t => t.batchId === batchId && (t.status === 'queued' || t.status === 'downloading'),
    );
  }

  private updateTasks(mutator: (tasks: DownloadTask[]) => DownloadTask[]): void {
    const next = mutator(this.tasks());
    this.tasks.set(next);
  }

  private downloadFromReadyEvent(task: DownloadTask): void {
    if (this.inFlightDownloads.has(task.requestId)) return;
    this.inFlightDownloads.add(task.requestId);

    this.updateTasks(list =>
      list.map(t => t.requestId === task.requestId ? { ...t, status: 'downloading' } : t),
    );

    this.batchService.fetchDownloadFile(task.requestId).subscribe({
      next: (blob: Blob) => {
        this.triggerBrowserDownload(blob, task.filename);
        this.updateTasks(list =>
          list.map(t => t.requestId === task.requestId ? { ...t, status: 'done' } : t),
        );
        this.inFlightDownloads.delete(task.requestId);
      },
      error: (err: Error) => {
        this.updateTasks(list =>
          list.map(t =>
            t.requestId === task.requestId
              ? { ...t, status: 'failed', reason: err?.message || 'File fetch failed' }
              : t,
          ),
        );
        this.inFlightDownloads.delete(task.requestId);
      },
    });
  }
}
